import React, { useMemo } from "react";
import { Card, Table, Tag, Button, Empty, Tooltip, Typography, Radio } from "antd";
import {
  TrendingUp,
  Target,
  BarChart3,
  DollarSign,
  Info,
  File,
  Award,
  PieChart,
} from "lucide-react";

import { Calendar, PlayCircle, Eye } from "lucide-react";
import type { ColumnsType } from "antd/es/table";
import axiosInstance from "../utils/axiosInstance";
import { Events } from "../constants/Event";
import mixpanel from "mixpanel-browser";

import {
  ChannelData,
  DatasetItem,
  ProcessedChannelData,
  TestItem,
} from "../models/interfaces";
import { useNavigate } from "react-router-dom";
const { Title } = Typography;

interface Props {
  dataset: DatasetItem;
  mmmTests?: TestItem[];
  selectedJobConfigId?: string | null;
  onSelect?: (jobConfigId: string) => void;
}

const MMMChannelROIDashboard: React.FC<Props> = ({
  dataset,
  mmmTests = [],
  selectedJobConfigId,
  onSelect,
}) => {
  const spendShareData = dataset?.spendShare?.[0] || {};
  const ModelAccuracy = dataset?.ModelAccuracy?.[0] || null;
  const handleExecute = async (jobConfigId: string): Promise<void> => {
    try {
      await axiosInstance.post(
        "/api/jobRun",
        { jobConfigId },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("authToken"),
          },
        }
      );
      mixpanel.track(Events.BUTTON_CLICK, {
        jobType: "MMM",
        action: "Execute",
        jobConfigId,
      });
      window.location.reload();
    } catch (error) {
      console.error("Error executing job:", error);
    }
  };
  const navigate = useNavigate();
  const handleViewResults = (test: TestItem): void => {
    navigate(`/jobs/MMM`, {
      state: {
        jobConfigId: test.jobConfigId,
        jobName: test.name,
        step: 2,
      },
    });
  };
  const handleNavigate = (path: string): void => {
    navigate(path);
  };
  const rawData: ChannelData[] = useMemo(() => {
    if (!dataset?.ROI_Final) return [];
    const saturationMap: Record<string, any> = {};
    Object.entries(dataset || {}).forEach(([key, value]) => {
      if (key.startsWith("Saturation_") && Array.isArray(value)) {
        const channel = key.replace("Saturation_", "");
        saturationMap[channel] = value[0]; // Assuming [saturationObject]
      }
    });

    return dataset.ROI_Final.filter(
      (item: any) => item.Spends > 0 || item.Contribution > 0
    ).map((item: any) => {
      let roi = 0;
      if (dataset?.spendShare?.[0]) {
        const spendShareObj = dataset.spendShare[0] as any;
        const possibleROIKeys = [
          `${item.MediaChannel}_ROI`,
          `${item.MediaChannel}_roi`,
          `ROI_${item.MediaChannel}`,
          `roi_${item.MediaChannel}`,
          item.MediaChannel,
        ];

        for (const key of possibleROIKeys) {
          if (spendShareObj[key] !== undefined) {
            roi = Number(spendShareObj[key]);
            break;
          }
        }

        if (roi === 0 && typeof item.ROAS === "number") {
          roi = item.ROAS;
        }
      } else {
        roi = typeof item.ROAS === "number" ? item.ROAS : 0;
      }
      const saturation = saturationMap[item.MediaChannel];
      return {
        channel: item.MediaChannel,
        metric: item.Spends,
        response: item.Contribution,
        ...(saturation || {}),
        efficiency: 100,
        roiCategory: "average",
        spendFormatted: `${roi.toFixed(2)}%`,
        responseFormatted: `${((item.Contribution / item.Spends) * 100).toFixed(
          2
        )}%`,
      };
    });
  }, [dataset]);

  const processedData: ProcessedChannelData[] = useMemo(() => {
    return rawData.map((item) => {
      const efficiency =
        item.metric > 0 ? (item.response / item.metric) * 100 : 0;
      let roiCategory: "excellent" | "good" | "average" | "poor";
      // Adjust thresholds based on whether ROI is in percentage or decimal format
      const roiValue =
        item.channelroi > 10 ? item.channelroi / 100 : item.channelroi;
      if (roiValue > 2) roiCategory = "excellent";
      else if (roiValue > 0.5) roiCategory = "good";
      else if (roiValue > 0.1) roiCategory = "average";
      else roiCategory = "poor";
      // For MMM data, we'll use the effect_share for the response formatting
      const roiItem = dataset?.ROI_Final?.find(
        (entry: any) => entry.MediaChannel === item.channel
      );

      const responseLabel = roiItem
        ? `${roiItem.effect_share.toFixed(2)}%`
        : item.response.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          });
      const spend = spendShareData[item.channel as keyof typeof spendShareData];
      const spendFormatted = typeof spend === "number" ? `${spend}%` : "0.00%";

      return {
        ...item,
        efficiency,
        roiCategory,
        spendFormatted,
        responseFormatted: responseLabel,
      };
    });
  }, [rawData, dataset, spendShareData]);

  const getRoiTagProps = (category: string) => {
    switch (category) {
      case "excellent":
        return { color: "green", label: "Excellent" };
      case "good":
        return { color: "blue", label: "Good" };
      case "average":
        return { color: "orange", label: "Average" };
      case "poor":
        return { color: "red", label: "Poor" };
      default:
        return { color: "default", label: "Unknown" };
    }
  };
  const tableColumns: ColumnsType<ProcessedChannelData> = [
    {
      title: () => (
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-gray-600" />
          <span className="text-gray-700 font-medium">Channel</span>
        </div>
      ),
      dataIndex: "channel",
      key: "channel",
      render: (text: string) => (
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 bg-blue-500 rounded-full"></div>
          <span className="font-medium text-gray-900 text-base">
            {text.replace("_", " ").replace("Spend", "")}
          </span>
        </div>
      ),
    },
    {
      title: () => (
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-gray-600" />
          <span className="text-gray-700 font-medium">Spend</span>
        </div>
      ),
      dataIndex: "spendFormatted",
      key: "spend",
      sorter: (a: ProcessedChannelData, b: ProcessedChannelData) =>
        a.metric - b.metric,
      render: (value: string) => (
        <div className="bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
          <span className="font-semibold text-gray-900">{value}</span>
        </div>
      ),
    },
    {
      title: () => (
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-gray-600" />
          <span className="text-gray-700 font-medium">Effect</span>
        </div>
      ),
      dataIndex: "responseFormatted",
      key: "response",
      sorter: (a: ProcessedChannelData, b: ProcessedChannelData) =>
        a.response - b.response,
      render: (value: string) => (
        <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-4 py-2 rounded-lg border border-gray-200">
          <span className="font-semibold text-gray-900">{value}</span>
        </div>
      ),
    },
    {
      title: () => (
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-gray-600" />
          <span className="text-gray-700 font-medium">ROI</span>
        </div>
      ),
      dataIndex: "channelroi",
      key: "roi",
      render: (value: number) => {
        const displayValue = value > 10 ? value : value * 100;
        const isPositive = displayValue > 0;

        return (
          <div className="flex items-center gap-2">
            <div
              className={`px-4 py-2 rounded-lg font-bold text-base ${
                isPositive
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-700 border border-gray-200"
              }`}
            >
              {displayValue.toFixed(2)}%
            </div>
          </div>
        );
      },
      sorter: (a: ProcessedChannelData, b: ProcessedChannelData) =>
        a.channelroi - b.channelroi,
    },
    {
      title: () => (
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-gray-600" />
          <span className="text-gray-700 font-medium">Performance</span>
        </div>
      ),
      dataIndex: "roiCategory",
      key: "performance",
      render: (category: string) => {
        const props = getRoiTagProps(category);

        // Professional, subtle color scheme with better contrast
        const styleMap: Record<
          string,
          { bg: string; text: string; border: string }
        > = {
          excellent: {
            bg: "bg-emerald-50",
            text: "text-emerald-700",
            border: "border-emerald-200",
          },
          good: {
            bg: "bg-blue-50",
            text: "text-blue-700",
            border: "border-blue-200",
          },
          average: {
            bg: "bg-amber-50",
            text: "text-amber-700",
            border: "border-amber-200",
          },
          poor: {
            bg: "bg-gray-50",
            text: "text-gray-700",
            border: "border-gray-200",
          },
        };

        const styles = styleMap[category.toLowerCase()] || styleMap.average;

        return (
          <div
            className={`inline-flex items-center px-3 py-1.5 rounded-md font-medium text-sm ${styles.bg} ${styles.text} border ${styles.border}`}
          >
            {props.label}
          </div>
        );
      },
    },
  ];
  const getMetricConfig = (key: any, value: any) => {
    const configs: any = {
      MAPE: {
        description: "Mean Absolute Percentage Error",
        icon: Target,
        color: "bg-green-500",
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        textColor: "text-green-700",
        status: "excellent",
        displayValue: `${(value * 100).toFixed(2)}%`,
        info: "MAPE evaluates prediction accuracy as a percentage, helping identify relative error regardless of the scale of the values being predicted.",
      },
      NRMSE: {
        description: "Normalized Root Mean Square Error",
        icon: BarChart3,
        color: "bg-blue-500",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200",
        textColor: "text-blue-700",
        status: "good",
        displayValue: `${(value * 100).toFixed(2)}%`,
        info: "NRMSE helps compare errors across datasets by normalizing RMSE, making it easier to interpret prediction quality over different scales.",
      },
      R_Squared: {
        description: "R-Squared (Coefficient of Determination)",
        icon: TrendingUp,
        color: "bg-purple-500",
        bgColor: "bg-purple-50",
        borderColor: "border-purple-200",
        textColor: "text-purple-700",
        status: "good",
        displayValue: `${(value * 100).toFixed(2)}%`,
        info: "R-Squared indicates how well data fit a regression model, measuring the proportion of variance explained by the independent variables.",
      },
      NSpend: {
        description: "Normalized Spend Efficiency",
        icon: DollarSign,
        color: "bg-indigo-500",
        bgColor: "bg-indigo-50",
        borderColor: "border-indigo-200",
        textColor: "text-indigo-700",
        status: "excellent",
        displayValue: `${(value * 100).toFixed(2)}%`,
        info: "Normalized Spend Efficiency shows how effectively resources are used, comparing performance relative to normalized budget or expenditure data.",
      },
    };

    return configs[key];
  };

  const getStatusBadge = (status: any) => {
    const badges: any = {
      perfect: "bg-emerald-100 text-emerald-800 border-emerald-200",
      excellent: "bg-green-100 text-green-800 border-green-200",
      good: "bg-blue-100 text-blue-800 border-blue-200",
      caution: "bg-orange-100 text-orange-800 border-orange-200",
    };

    const labels: any = {
      perfect: "Perfect",
      excellent: "Excellent",
      good: "Good",
      caution: "Needs Attention",
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full border ${badges[status]}`}
      >
        {labels[status]}
      </span>
    );
  };
  const proceesedMmmJob = useMemo(() => {
    return mmmTests.find((ele) => ele.jobConfigId === selectedJobConfigId);
  }, [mmmTests, selectedJobConfigId]);

  const isDatasetEmpty =
    !dataset || !dataset.ROI_Final || dataset.ROI_Final.length === 0;

  return (
    <div className="">
      <div className="mb-5">
        <div className="w-full mb-2 md:mb-5 flex justify-center">
          <div className="bg-gradient-to-r from-indigo-50 via-white to-indigo-50 rounded-2xl shadow-lg p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 w-full border border-indigo-100 animate-fadeIn">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="p-3 bg-indigo-100 rounded-full">
                  <PieChart className="h-8 w-8 text-indigo-600" />
                </div>
              </div>
              <div>
                <Title level={1} className="m-0">
                  Marketing Mix Results
                </Title>
                <p className="text-base mt-[-10px] text-lg text-gray-600">
                  {proceesedMmmJob?.name || "No test selected"}
                </p>
              </div>
            </div>

            {/* Right Section (Buttons) */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleNavigate("/jobs/MMM")}
                className="px-4 py-2 bg-[#0055B8] text-white rounded-lg shadow hover:bg-[#003D82] transition"
              >
                Create a new job
              </button>
              <button
                onClick={() => handleNavigate("/planning/marketing-mix-model")}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg shadow hover:bg-gray-200 transition"
              >
                View All
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg shadow-sm">
            <Card
              title={
                <span className="text-lg font-medium">
                  Historical Media Mix Results
                </span>
              }
              className="h-full"
              bodyStyle={{ padding: "12px" }}
            >
              {mmmTests.length === 0 ? (
                <Empty
                  description="No completed tests available"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  className="my-4"
                  imageStyle={{ height: 40 }}
                />
              ) : (
                <div className="space-y-2">
                  {mmmTests
                    .slice(0, 5)
                    .map((test, index) => (
                      <div
                        key={`${test.jobConfigId}-${index}`}
                        className="flex items-center gap-3 border-b pb-2 last:border-b-0 cursor-pointer"
                        onClick={() => onSelect?.(test.jobConfigId)}
                      >
                        <Radio checked={selectedJobConfigId === test.jobConfigId} />
                        <div className="flex-1 min-w-0">
                          <p className="text-md font-medium text-gray-800 truncate">
                            {test.name}
                          </p>
                          <div className="flex items-center text-md text-gray-400 mt-1">
                            <Calendar className="w-2 h-2 mr-1 flex-shrink-0" />
                            <span className="text-md">{test.date}</span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 ml-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewResults(test); }}
                            className="flex items-center gap-1 text-white text-xs px-3 py-1 rounded bg-[#0055B8] hover:bg-[#003D82] whitespace-nowrap flex-shrink-0"
                          >
                            <Eye className="w-3 h-3" />
                            View Results
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </Card>
          </div>
          <div className="col-span-12 md:col-span-8 space-y-4">
            <div className="bg-white rounded-lg shadow-sm">
              <Table
                dataSource={processedData}
                columns={tableColumns}
                rowKey="channel"
                pagination={false}
                className="highlighted-table"
              />
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-1">
                  Performance Metrics
                </h2>
                <p className="text-sm text-gray-600">
                  Model performance indicators and efficiency metrics
                </p>
              </div>
              {ModelAccuracy && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(ModelAccuracy)
                    .filter(([key]) => key !== "NNRMSE" && key !== "NError")
                    .map(([key, value]: [any, any]) => {
                      const config = getMetricConfig(key, value);
                      const IconComponent = config?.icon ?? File;

                      return (
                        <div
                          key={key}
                          className={`${config?.bgColor} ${config?.borderColor} border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow duration-200`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className={`flex items-center `}>
                              <div
                                className={`${config?.color} p-1.5 rounded-lg shadow-sm`}
                              >
                                {IconComponent ? (
                                  <IconComponent className="h-3 w-3 text-white" />
                                ) : (
                                  <div className="h-3 w-3 text-white" />
                                )}
                              </div>
                              <h3
                                className={`text-sm font-semibold ml-2 ${config?.textColor}`}
                              >
                                {key === "R_Squared" ? "R²" : key}
                              </h3>
                            </div>
                            <div className="flex items-center">
                              {getStatusBadge(config?.status)}
                              <Tooltip title={config?.info}>
                                <Info className="h-3 ml-2 w-3 text-gray-500 hover:text-gray-700 cursor-pointer" />
                              </Tooltip>
                            </div>
                          </div>

                          <div className="mb-2">
                            <div className="flex items-center gap-1"></div>
                            <p className="text-xs text-gray-600">
                              {config?.description}
                            </p>
                          </div>

                          <div className="space-y-1">
                            <div
                              className={`text-lg font-bold ${config?.textColor}`}
                            >
                              {config?.displayValue}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
              {isDatasetEmpty && (
                <Empty
                  description="No dataset available. Please run a test to see channel ROI analysis."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  className="my-8"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MMMChannelROIDashboard;
